import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getFeaturedEvent,
  getRegisteredEvents,
  getUpcomingEvents,
  searchEvents,
  getEventDetail,
  getEventRegistrationStatus,
  registerForEvent,
  unregisterFromEvent,
} from '@/api/events.api';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { eventsKeys } from './events.keys';

export function useFeaturedEvent() {
  return useQuery({
    queryKey: eventsKeys.featured(),
    queryFn: getFeaturedEvent,
    staleTime: 60_000,
  });
}

export function useRegisteredEvents() {
  return useQuery({
    queryKey: eventsKeys.registered(),
    queryFn: getRegisteredEvents,
    staleTime: 60_000,
  });
}

export function useUpcomingEvents(page: number) {
  return useQuery({
    queryKey: eventsKeys.upcoming(page),
    queryFn: () => getUpcomingEvents(page),
    staleTime: 60_000,
  });
}

/** Disabled when q is empty or whitespace-only — no blank query is ever sent to the backend. */
export function useSearchEvents(q: string, includePast: boolean, page: number) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: eventsKeys.search(trimmed, includePast, page),
    queryFn: () => searchEvents(trimmed, includePast, page),
    enabled: trimmed.length > 0,
    staleTime: 60_000,
  });
}

export function useEventDetail(id: string) {
  return useQuery({
    queryKey: eventsKeys.detail(id),
    queryFn: () => getEventDetail(id),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

/**
 * Fetch whether the authenticated caller is registered for an event.
 * Backed by the authenticated GET /v1/events/:eventId/register endpoint — kept
 * separate from the public detail query so the public detail route stays
 * viewer-agnostic (no guard change required).
 *
 * @param eventId Target event.
 * @param enabled Pass the caller's auth state. The event detail page is now
 *   reachable logged out, where this endpoint would answer 401 - a failed
 *   request whose only possible answer is "not registered", which the page
 *   already assumes by default. Skipping it there keeps a 401 out of the
 *   console and out of the query cache.
 */
export function useEventRegistrationStatus(eventId: string, enabled = true) {
  return useQuery({
    queryKey: eventsKeys.registrationStatus(eventId),
    queryFn: () => getEventRegistrationStatus(eventId),
    enabled: Boolean(eventId) && enabled,
    staleTime: 30_000,
  });
}

/** Register the authenticated user for an event. */
export function useRegisterForEvent(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => registerForEvent(eventId),
    onSuccess: () => {
      toast.success("You're registered!");
      void queryClient.invalidateQueries({ queryKey: eventsKeys.registrationStatus(eventId) });
      void queryClient.invalidateQueries({ queryKey: eventsKeys.registered() });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Could not register for this event. Please try again.'));
    },
  });
}

/** Unregister the authenticated user from an event. */
export function useUnregisterFromEvent(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unregisterFromEvent(eventId),
    onSuccess: () => {
      toast.success("You've been unregistered.");
      void queryClient.invalidateQueries({ queryKey: eventsKeys.registrationStatus(eventId) });
      void queryClient.invalidateQueries({ queryKey: eventsKeys.registered() });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Could not unregister from this event. Please try again.'));
    },
  });
}
